import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { EquipmentService } from '../../../services/equipment/equipment.service';

@Component({
  selector: 'app-serial-range-selection',
  templateUrl: './serial-range-selection.component.html',
  standalone: true,
  imports: [MatIcon, CommonModule, FormsModule, MatFormField, MatLabel, MatInputModule],
  styleUrls: ['./serial-range-selection.component.css']
})
export class SerialRangeSelectionComponent {
  serialExclusions: { from?: string; to?: string; value?: string }[] = [];
  serialRanges: { from: string; to: string }[] = [{ from: '', to: '' }];
  serialStart: string = '';
  serialEnd: string = '';
  pvc: string = '';
  isPVCMake: boolean = false;
  selectedMode: 'PVC' | 'RANGE' | null = null;
  pvcMakes: string[] = [];
  selectedMake: string = '';

  constructor(
    private sharedData: EquipmentService,
    public dialogRef: MatDialogRef<SerialRangeSelectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    // This block will subscribe to the pvc makes loaded from either the admin makes or tech makes
    this.sharedData.sharedPVCMakes$.subscribe(value => {
      this.pvcMakes = value;
      //console.log("Received pvc makes:", value); //Uncomment for debugging
    });
    // This block will subscribe to the make selected by the user in resources component
    this.sharedData.sharedSelectedMake$.subscribe(value => {
      this.selectedMake = value;
      //console.log("Received make is:", value); //Uncomment for debugging
    })
    // Now instead of hard coding this condition for JLG, we check if the make selected is in pvc makes list
    this.isPVCMake = this.pvcMakes.includes(this.selectedMake);
  }

  onPvcChange(value: string): void {
    this.pvc = value;

    if (this.selectedMode === 'RANGE' && value?.trim()) {
      alert('Clear serial ranges to enter PVC.');
      this.pvc = '';
      return;
    }

    if (!this.selectedMode && value?.trim()) {
      this.selectedMode = 'PVC';
    }

    this.updateModeAfterClear();
  }

  onRangeChange(): void {
    const anyRangeEntered = this.serialRanges.some(r => (r.from?.trim() || r.to?.trim()));

    if (this.selectedMode === 'PVC' && anyRangeEntered) {
      alert('Clear PVC to enter serial ranges.');
      this.serialRanges = this.serialRanges.map(r => ({ from: '', to: '' }));
      return;
    }

    if (!this.selectedMode && anyRangeEntered) {
      this.selectedMode = 'RANGE';
    }

    this.updateModeAfterClear();
  }

  private updateModeAfterClear(): void {
    const pvcEmpty = !this.pvc?.trim();
    const allRangesEmpty = !this.serialRanges.some(r => r.from?.trim() || r.to?.trim());

    if (pvcEmpty && allRangesEmpty) {
      this.selectedMode = null;
    }
  }

  handleDisabledClick(target: 'PVC' | 'RANGE'): void {
    if (target === 'PVC' && this.selectedMode === 'RANGE') {
      alert('Clear serial ranges to enter PVC.');
    } else if (target === 'RANGE' && this.selectedMode === 'PVC') {
      alert('Clear PVC to enter serial ranges.');
    }
  }

  addIndividualExclusion(): void {
    this.serialExclusions.push({ value: '' });
  }

  addRangeExclusion(): void {
    this.serialExclusions.push({ from: '', to: '' });
  }

  addSerialRange(): void {
    this.serialRanges.push({ from: '', to: '' });
  }

  removeExclusion(index: number): void {
    this.serialExclusions.splice(index, 1);
  }

  removeSerialRange(index: number): void {
    this.serialRanges.splice(index, 1);
    this.onRangeChange(); 
  }

  confirm(): void {
    const exclusionsFormatted = this.serialExclusions
      .map((excl) =>
        excl.value
          ? excl.value
          : excl.from && excl.to
          ? `${excl.from}-${excl.to}`
          : ''
      )
      .filter(Boolean);

    const isExclusionValid = this.serialExclusions.every((excl) => {
      if (excl.from && excl.to) {
        return this.serialRanges.some(
          (range) =>
            (excl.from ?? '') >= range.from && (excl.to ?? '') <= range.to
        );
      }
      return true;
    });

    if (!isExclusionValid) {
      alert('Exclusion entered is not within the serial range');
      return;
    }

    const anyRangeEntered = this.serialRanges.some(
      (range) => range.from || range.to
    );

    const hasInvalidRange = this.serialRanges.some(
      (range) =>
        (range.from && !range.to) ||
        (!range.from && range.to)
    );

    const pvcEntered = this.isPVCMake && !!this.pvc?.trim();

    if (hasInvalidRange) {
      alert('Please fill in Serial range "From" and "To" fields.');
      return;
    }

    const hasCompleteRange = this.serialRanges.some(
      (range) => !!range.from && !!range.to
    );

    if (!this.isPVCMake && !hasCompleteRange) {
      alert('Please fill in Serial range "From" and "To" fields.');
      return;
    }
    if (!pvcEntered && !hasCompleteRange) {
      alert('Please enter either PVC or a complete Serial range.');
      return;
    }

    this.dialogRef.close({
      serialRanges: this.serialRanges,
      serialExclusions: exclusionsFormatted,
      pvc: this.isPVCMake ? this.pvc : null,
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
